import React, { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import AdminLayout from '@components/layout/AdminLayout.tsx';
import { PermissionTypesEnum } from '@mock/Permissions.ts';
import TablePagination from '@components/pagination/TablePagination.tsx';
import { Button } from '@components/ui/Button.tsx';
import { usePageBoundsGuard } from '@hooks/usePageBoundsGuard.ts';
import { formatDate, formatDateTime, formatMoney } from '@utils/formats.ts';
import { Route } from '@routes/admin/requisitions/index.tsx';
import { RequisitionStatus } from '@api/generated/portal-be-admin';
import { useAdminRequisitionStore } from './useAdminRequisitionStore.ts';
import RequisitionDetailModal from './RequisitionDetailModal.tsx';

const STATUS_OPTIONS: RequisitionStatus[] = [
  RequisitionStatus.Submitted,
  RequisitionStatus.Processed,
  RequisitionStatus.Rejected,
  RequisitionStatus.Cancelled,
];

/** Přehled žádanek přes všechny zákazníky — jen čtení, zpracovává je řešitel u zákazníka. */
const RequisitionsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const [modalVisible, setModalVisible] = useState(false);

  const data = useAdminRequisitionStore(s => s.data);
  const loading = useAdminRequisitionStore(s => s.loading);
  const error = useAdminRequisitionStore(s => s.error);
  const pagination = useAdminRequisitionStore(s => s.pagination);
  const loadDetail = useAdminRequisitionStore(s => s.loadDetail);
  const clearDetail = useAdminRequisitionStore(s => s.clearDetail);

  // Lokální stav pro editaci filtrů, aplikuje se až tlačítkem (viz CLAUDE.md).
  const [localQ, setLocalQ] = useState(search.q ?? '');
  const [localStatus, setLocalStatus] = useState<RequisitionStatus | undefined>(search.status);

  useEffect(() => {
    setLocalQ(search.q ?? '');
    setLocalStatus(search.status);
  }, [search.q, search.status]);

  const applyFilters = () => navigate({ search: { ...search, q: localQ || undefined, status: localStatus, page: 1 } });
  const clearFilters = () => {
    setLocalQ('');
    setLocalStatus(undefined);
    navigate({ search: { page: 1, size: search.size } });
  };

  const handleRowClick = (requisitionId: number) => {
    loadDetail(requisitionId);
    setModalVisible(true);
  };

  const handleModalHide = () => {
    setModalVisible(false);
    clearDetail();
  };

  const handlePageChange = (newPage: number) => {
    navigate({ search: { ...search, page: newPage } });
  };

  usePageBoundsGuard(pagination?.page, pagination?.totalPages, handlePageChange);

  const th = 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

  return (
    <AdminLayout oneOfPermission={[PermissionTypesEnum.ADMIN_REQUISITION_READ]} title={t('requisitions.title')}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('requisitions.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('requisitions.subtitle')}</p>
        </div>

        <div className="flex flex-wrap items-end gap-3 bg-white rounded-lg border border-gray-200 p-4">
          <div className="min-w-[260px] flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('requisitions.filters.q')}</label>
            <input
              type="text"
              value={localQ}
              onChange={e => setLocalQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('requisitions.columns.status')}</label>
            <select
              value={localStatus ?? ''}
              onChange={e => setLocalStatus((e.target.value || undefined) as RequisitionStatus | undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">—</option>
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>
                  {t(`requisitions.status.${status}`)}
                </option>
              ))}
            </select>
          </div>
          <Button size="md" type="primary" variant="fill" label={t('requisitions.filters.apply')} onClick={applyFilters} />
          <Button size="md" type="secondary" variant="outline" label={t('requisitions.filters.clear')} onClick={clearFilters} />
        </div>

        {loading && (
          <div className="flex justify-center items-center p-8">
            <div className="text-lg">{t('requisitions.loading')}</div>
          </div>
        )}

        {error && !loading && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              {pagination && <p className="text-sm text-gray-600">{t('requisitions.total', { total: pagination.total })}</p>}
            </div>

            <div className="overflow-x-auto">
              {!data || data.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p className="text-lg">{t('requisitions.empty')}</p>
                </div>
              ) : (
                <>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={th}>{t('requisitions.columns.number')}</th>
                        <th className={th}>{t('requisitions.columns.organization')}</th>
                        <th className={th}>{t('requisitions.columns.supplier')}</th>
                        <th className={th}>{t('requisitions.columns.author')}</th>
                        <th className={th}>{t('requisitions.columns.portal')}</th>
                        <th className={th}>{t('requisitions.columns.deliveryDate')}</th>
                        <th className={th}>{t('requisitions.columns.total')}</th>
                        <th className={th}>{t('requisitions.columns.status')}</th>
                        <th className={th}>{t('requisitions.columns.order')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.map(requisition => (
                        <tr key={requisition.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(requisition.id)}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{requisition.id}</div>
                            <div className="text-xs text-gray-400">{formatDateTime(requisition.created)}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{requisition.organizationName}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{requisition.supplierName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{requisition.userName || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{requisition.portalName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(requisition.deliveryDate)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatMoney(requisition.totalWithoutVat)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t(`requisitions.status.${requisition.status}`)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{requisition.orderNumber || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {pagination && (
                    <TablePagination
                      page={pagination.page}
                      totalPages={pagination.totalPages}
                      total={pagination.total}
                      pageSize={pagination.size}
                      onPageChange={handlePageChange}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
        <RequisitionDetailModal visible={modalVisible} onHide={handleModalHide} />
      </div>
    </AdminLayout>
  );
};

export default RequisitionsPage;
